import { useMemo, type CSSProperties } from 'react';

import { useAppSelector } from '@/store/hooks';
import { selectStylepacks } from '@/store/slices/stylepacks/selectors';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import getThemeColorsCssMixVariable from '@/utils/colors/get-theme-color-css-mix-variable';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import Button from '@ui/buttons/button/button';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import Link from '@ui/links/link';

import styles from './empty-stylepack-view.module.css';

export default function EmptyStylepackView() {
  const stylepacks = useAppSelector(selectStylepacks);

  const images = useMemo(() => {
    return (
      stylepacks?.map((pack) => {
        return (pack.videos.find((vid) => vid.contentType === 'walkup') || pack.videos[0]).thumbnailUrl;
      }) ?? []
    );
  }, [stylepacks]);

  return (
    <Container
      className="flex ai-center jc-center flex-column gap-40 position-relative"
      width="100%"
      maxWidth="992px"
      design={'GlobalCont1'}
      borderRadius="16px"
      aspectRatio="992/708"
    >
      <div
        className={styles.background}
        style={
          {
            // these are in the order set to match the designs - if they are reordered
            // in greenroom they will be reordered here as well
            '--bg1': `url(${images[4]})`,
            '--bg2': `url(${images[6]})`,
            '--bg3': `url(${images[1]})`,
            '--bg4': `url(${images[5]})`,
            '--bg5': `url(${images[0]})`,
          } as CSSProperties
        }
      >
        <div className={styles.img1} />
        <div className={styles.img2} />
        <div className={styles.img3} />
        <div className={styles.img4} />
        <div className={styles.img5} />
        <div className={styles.bloom} />
      </div>
      <Container className="flex flex-column gap-12 ai-center">
        <Text className="H2" tag="h2" color="var(--project-theme-color-primary)">
          Stylepacks
          <Text tag="sup" fontSize="15px" className="font-weight-900">
            TM
          </Text>
        </Text>
        <Text className="S1">Unleash Your Creativity</Text>
      </Container>
      <Link to={'./browse'} textColor="none" viewTransition viewTransitionType="slide-up">
        <Button
          backgroundColor={getThemeColorCSSVariable()}
          hoverBackgroundColor={getThemeColorsCssMixVariable()}
          designSize="large"
        >
          <Text
            color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
            className="L1"
          >
            Browse Stylepacks
          </Text>
        </Button>
      </Link>
      <Text className="L3 position-absolute bottom-29 left-0 right-0 text-center">
        Effortless design with Stylepacks — premium presets for every style, ready to use.
      </Text>
    </Container>
  );
}
